interface GeospatialCachedMatch {
  lat: number;
  lng: number;
  matchedCity: string;
  matchedCountry: string;
  source: string;
  ts: number;
}

const SESSION_STORAGE_KEY = 'fractal.geospatial.matchCache.v1';
const inMemoryCache = new Map<string, GeospatialCachedMatch>();
let hydrated = false;

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function hydrateFromSessionStorage(): void {
  if (hydrated || !canUseSessionStorage()) return;
  hydrated = true;
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, GeospatialCachedMatch>;
    for (const [key, value] of Object.entries(parsed)) {
      if (!value || typeof value !== 'object') continue;
      if (
        typeof value.lat !== 'number' ||
        typeof value.lng !== 'number' ||
        typeof value.matchedCity !== 'string' ||
        typeof value.matchedCountry !== 'string' ||
        typeof value.source !== 'string' ||
        typeof value.ts !== 'number'
      ) {
        continue;
      }
      inMemoryCache.set(key, value);
    }
  } catch {
    /* ignore malformed cache */
  }
}

function persistToSessionStorage(): void {
  if (!canUseSessionStorage()) return;
  try {
    const payload = Object.fromEntries(inMemoryCache.entries());
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* best effort only */
  }
}

export function normalizeGeospatialPlaceKey(
  location: string | undefined | null,
  country: string | undefined | null
): string {
  const loc = (location || '').split(',')[0].trim().toLowerCase();
  const cty = (country || '').trim().toLowerCase();
  return `${loc}|${cty}`;
}

export function readGeospatialMatchCache(
  placeKey: string
): GeospatialCachedMatch | null {
  hydrateFromSessionStorage();
  return inMemoryCache.get(placeKey) ?? null;
}

export function writeGeospatialMatchCache(placeKey: string, value: Omit<GeospatialCachedMatch, 'ts'>): void {
  hydrateFromSessionStorage();
  inMemoryCache.set(placeKey, {
    ...value,
    ts: Date.now(),
  });
  persistToSessionStorage();
}
